import { API_BASE_URL } from '@/services/api';

export interface RollupJobResult {
  consensus_lines_touched: number;
}

export interface RollupJobStatus {
  status: 'idle' | 'running' | 'completed' | 'failed';
  planning_cycle_id: number;
  trigger?: string;
  started_at?: string;
  finished_at?: string;
  result?: RollupJobResult;
  error?: string;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok) {
    const message =
      (typeof data.detail === 'string' && data.detail) ||
      (typeof data.message === 'string' && data.message) ||
      'Rollup error';
    const err = new Error(message) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return data as T;
}

async function authHeaders(): Promise<Headers> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  const token = localStorage.getItem('auth_token');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

export async function fetchRollupJobStatus(cycleId: number): Promise<RollupJobStatus> {
  const headers = await authHeaders();
  const response = await fetch(
    `${API_BASE_URL}/demand-planning/planning-cycles/${cycleId}/rollup-status/`,
    { method: 'GET', headers },
  );
  return parseJsonResponse<RollupJobStatus>(response);
}

export async function pollRollupJobUntilDone(
  cycleId: number,
  options?: { intervalMs?: number; maxAttempts?: number },
): Promise<RollupJobResult> {
  const intervalMs = options?.intervalMs ?? 2000;
  const maxAttempts = options?.maxAttempts ?? 120;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const job = await fetchRollupJobStatus(cycleId);
    if (job.status === 'completed' && job.result) {
      return job.result;
    }
    if (job.status === 'failed') {
      throw new Error(job.error || 'Rollup failed');
    }
    if (job.status === 'idle' && attempt > 2) {
      throw new Error('Rollup job not found or already finished');
    }
    await sleep(intervalMs);
  }
  throw new Error('Rollup timed out');
}

export async function waitForRollupJob(
  cycleId: number,
  rollupJob?: RollupJobStatus,
): Promise<void> {
  if (rollupJob?.status === 'running') {
    await pollRollupJobUntilDone(cycleId);
  }
}
